import styled, { keyframes } from "styled-components";

/* ═══════════════════════════════════════════════════════════════════════
   CollectionSettingsModal — LIGHT WATER PROMPT-VIEWER COMPOSITION
   Shows the prompt text in a frosted inner card with a subtle scroll area.
   Header: brand-blue title on the left, frosted water "Copy" circle on
   the right (same handler as before — clicking it copies + closes).
   Bottom: prominent "Copy Prompt" primary CTA.
   ═══════════════════════════════════════════════════════════════════════ */

const panelFloat = keyframes`
  0%, 100% { filter: brightness(1); }
  50%      { filter: brightness(1.02); }
`;

const buttonShimmer = keyframes`
  0%   { left: -100%; }
  100% { left: 150%; }
`;

// ─── Shell ─────────────────────────────────────────────────────────────

export const ModalContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: min(92dvw, 480px);
  max-height: 90dvh;
  overflow: hidden;
  border-radius: 28px;
  border: 1px solid var(--glass-edge);
  background:
    radial-gradient(
      120% 60% at 30% -10%,
      rgba(var(--glass-white-rgb), 0.96) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 55%,
      rgba(var(--glass-pale-rgb), 0.82) 100%
    ),
    linear-gradient(
      180deg,
      rgba(var(--glass-white-rgb), 0.94) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 100%
    );
  backdrop-filter: blur(28px) saturate(180%);
  -webkit-backdrop-filter: blur(28px) saturate(180%);
  box-shadow:
    0 32px 64px -18px rgba(var(--color-brand-500-rgb), 0.4),
    0 12px 28px rgba(var(--color-brand-400-rgb), 0.22),
    inset 0 2px 0 rgba(255, 255, 255, 0.95),
    inset 0 -10px 22px rgba(var(--color-brand-200-rgb), 0.26),
    inset 0 0 0 1px rgba(255, 255, 255, 0.45);
  animation: ${panelFloat} 5.5s ease-in-out infinite;

  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 32px;
    right: 32px;
    height: 18px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.9) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(2px);
    pointer-events: none;
    z-index: 10;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  @media (max-width: 475px) {
    border-radius: 24px;
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

export const ModalContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 22px 0 20px;
  gap: 14px;
  max-height: 82dvh;

  @media (max-width: 475px) {
    padding: 18px 0 16px;
    gap: 12px;
  }
`;

export const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 0 22px;

  @media (max-width: 475px) {
    padding: 0 18px;
  }
`;

export const ModalTitle = styled.h2`
  margin: 0;
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: normal;
  user-select: none;
`;

/* Header action — frosted water circle hosting the Copy glyph.
   Clicking it still fires `onCopy` (preserves existing behavior). */
export const CloseButton = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px solid rgba(var(--glass-white-rgb), 0.7);
  background: radial-gradient(
    130% 100% at 30% 15%,
    rgba(var(--glass-white-rgb), 0.85) 0%,
    rgba(var(--glass-white-rgb), 0.58) 45%,
    rgba(var(--glass-pale-rgb), 0.42) 100%
  );
  backdrop-filter: blur(18px) saturate(180%);
  -webkit-backdrop-filter: blur(18px) saturate(180%);
  box-shadow:
    0 6px 14px rgba(var(--color-brand-500-rgb), 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -3px 8px rgba(var(--color-brand-200-rgb), 0.2);
  cursor: pointer;
  outline: none;
  overflow: hidden;
  color: var(--color-brand-700);
  transition:
    transform 200ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy);

  &::before {
    content: "";
    position: absolute;
    top: 4px;
    left: 7px;
    width: 16px;
    height: 9px;
    border-radius: 50%;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.88) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  svg {
    width: 16px;
    height: 16px;
    position: relative;
    z-index: 1;
    color: var(--color-brand-700);
    path {
      stroke: var(--color-brand-700);
    }
  }

  &:hover {
    transform: translateY(-1px) scale(1.05);
    border-color: rgba(var(--color-brand-400-rgb), 0.55);
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

/* Inner frosted card that hosts the prompt text block */
export const PromptCard = styled.div`
  position: relative;
  margin: 0 22px;
  padding: 16px 18px;
  border-radius: 18px;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.24);
  background: radial-gradient(
    120% 80% at 30% -10%,
    rgba(var(--glass-white-rgb), 0.9) 0%,
    rgba(var(--glass-pale-rgb), 0.78) 55%,
    rgba(var(--glass-pale-rgb), 0.66) 100%
  );
  backdrop-filter: blur(12px) saturate(160%);
  -webkit-backdrop-filter: blur(12px) saturate(160%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.92),
    inset 0 -2px 8px rgba(var(--color-brand-200-rgb), 0.16),
    0 2px 6px rgba(var(--color-brand-500-rgb), 0.08);
  max-height: 48dvh;
  overflow-y: auto;

  scrollbar-width: thin;
  scrollbar-color: rgba(var(--color-brand-300-rgb), 0.4) transparent;
  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(var(--color-brand-300-rgb), 0.4);
    border-radius: 999px;
  }

  @media (max-width: 475px) {
    margin: 0 18px;
    padding: 14px 15px;
  }
`;

export const Label = styled.p`
  margin: 0;
  color: var(--color-brand-700);
  opacity: 0.88;
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 500;
  line-height: normal;
  letter-spacing: -0.005em;
  text-align: left;
  user-select: text;
  white-space: pre-wrap;
  word-break: break-word;
`;

/* Footer button row — Copy Prompt primary CTA */
export const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
  padding: 10px 22px 0;
  border-top: 1px solid rgba(var(--color-brand-300-rgb), 0.18);
  margin-top: 2px;

  @media (max-width: 475px) {
    padding: 10px 18px 0;
  }
`;

export const ConfirmButton = styled.button`
  position: relative;
  display: flex;
  flex: 1;
  min-height: 48px;
  padding: 0 22px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 999px;
  border: 1px solid rgba(var(--glass-white-rgb), 0.72);
  background: linear-gradient(
    180deg,
    var(--color-brand-400) 0%,
    var(--color-brand-600) 55%,
    var(--color-brand-700) 100%
  );
  box-shadow:
    0 14px 28px rgba(var(--color-brand-600-rgb), 0.36),
    inset 0 1px 0 rgba(255, 255, 255, 0.55),
    inset 0 -3px 6px rgba(var(--color-brand-700-rgb), 0.3);
  color: var(--glass-text-on-pill);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 800;
  letter-spacing: -0.005em;
  text-shadow: 0 1px 1px rgba(var(--color-brand-900-rgb), 0.28);
  cursor: pointer;
  outline: none;
  overflow: hidden;
  transition:
    transform 200ms var(--ease-snappy),
    filter 200ms var(--ease-snappy),
    box-shadow 200ms var(--ease-snappy);

  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 16px;
    right: 16px;
    height: 9px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.72) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1.5px);
    pointer-events: none;
    z-index: 1;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 45%;
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.38) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    animation: ${buttonShimmer} 4s infinite;
    pointer-events: none;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.06);
    box-shadow:
      0 18px 34px rgba(var(--color-brand-600-rgb), 0.46),
      inset 0 1px 0 rgba(255, 255, 255, 0.7),
      inset 0 -3px 8px rgba(var(--color-brand-700-rgb), 0.38);
  }

  svg {
    width: 16px;
    height: 16px;
    position: relative;
    z-index: 2;
    path {
      stroke: #ffffff;
    }
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

/* Legacy exports kept for any callers that import them */
export const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 8px;
  padding: 0 22px;
`;

export const SelectWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const Select = styled.select`
  display: flex;
  padding: 12px;
  padding-right: 40px;
  width: 100%;
  border-radius: 12px;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.28);
  background: var(--glass-base-2);
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  outline: none;
`;

export const ChevronIcon = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-brand-700);
  svg {
    width: 12px;
    height: 6px;
  }
`;

export const CancelButton = styled.button`
  display: flex;
  min-width: 110px;
  min-height: 46px;
  padding: 0 20px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 999px;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.26);
  background: var(--glass-base-2);
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
`;

export const Input = styled.input`
  width: 100%;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.28);
  background: var(--glass-base-2);
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 14px;
`;
